// import components
import { Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxFlexCenterGapFullWidth,
  StyledBoxFlexCenterSmallGap,
  StyledBoxFlexCenterSmallGapFullWidth,
  StyledBoxFlexColumnNoGap
} from 'components/shared/Box/Box.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import dayjs from 'dayjs';

// import hooks
import { useTranslation } from 'react-i18next';
import {
  StyledNotificationDot,
  StyledPercentageBarContainer,
  StyledPercentageBarContainerBar,
  StyledPercentageLabelTypography
} from './useGetEmployeesColumns.styles';

// import types
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

export const useGetEmployeesColumns = () => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch.columns'
  });
  const { t: tCommon } = useTranslation('common');

  return [
    {
      headerName: t('availability'),
      field: 'availablePercentage',
      type: 'string',
      minWidth: 140,
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) return null;

        return (
          <StyledBoxFlexCenterGapFullWidth>
            <StyledPercentageLabelTypography variant="body2" component="span">
              {params.value !== undefined ? `${params.value}%` : ''}
            </StyledPercentageLabelTypography>
            <StyledPercentageBarContainer params={params}>
              <StyledPercentageBarContainerBar params={params} />
            </StyledPercentageBarContainer>
          </StyledBoxFlexCenterGapFullWidth>
        );
      }
    },
    {
      headerName: t('overlap'),
      field: 'dutyOverlap',
      type: 'string',
      valueFormatter: (value) => (value ? tCommon('yes') : tCommon('no'))
    },
    {
      headerName: tCommon('name'),
      field: 'employeeName',
      type: 'string',
      flex: 1
    },
    {
      headerName: tCommon('source'),
      field: 'sourceCostCenter',
      type: 'string',
      flex: 1
    },
    {
      headerName: tCommon('function'),
      field: 'functionName',
      type: 'string'
    },
    {
      headerName: t('balance'),
      field: 'balanceHours',
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { value } = params;
        if (value === null || value === undefined) return '-';

        const rounded = -Math.floor(value * 100) / 100;
        return (
          <Tooltip
            title={t('workedTooLittle')}
            arrow
            disableHoverListener={rounded >= 0}
          >
            <Typography
              variant="body2"
              component="span"
              color={rounded < 0 ? 'error' : 'inherit'}
            >
              {Number.isInteger(rounded) ? rounded : rounded.toFixed(2)}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      headerName: tCommon('wa'),
      field: 'workAgreementNr',
      type: 'string',
      align: 'center',
      headerAlign: 'center'
    },
    {
      headerName: t('relation'),
      field: 'workRelation',
      type: 'string',
      align: 'center',
      headerAlign: 'center'
    },
    {
      headerName: t('operations'),
      field: 'operation',
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isPreferredFlexPlanner = params.row.matchCandidateTypes?.includes(
          'PREFERRED_FLEX_PLANNER'
        );
        const isReplied =
          params.row.matchCandidateTypes?.includes('REPLIED_AVAILABLE') ||
          params.row.matchCandidateTypes?.includes('REPLIED_NOT_AVAILABLE');

        return (
          <StyledBoxFlexCenterSmallGap>
            {isPreferredFlexPlanner && (
              <Tooltip title={t('preferredFlexPlanner')} arrow>
                <IconContainer iconSize="MEDIUM">
                  <Icon type="star-fill" color="action" />
                </IconContainer>
              </Tooltip>
            )}

            {isReplied && (
              <Tooltip title={t('asked')} arrow>
                <IconContainer iconSize="MEDIUM">
                  <Icon type="comments" color="action" />
                </IconContainer>
              </Tooltip>
            )}
          </StyledBoxFlexCenterSmallGap>
        );
      }
    },
    {
      headerName: t('response'),
      field: 'matchRemark',
      type: 'string',
      renderCell: (params) => {
        const isAvailable =
          params.row.matchCandidateTypes?.includes('REPLIED_AVAILABLE');
        const isNotAvailable = params.row.matchCandidateTypes?.includes(
          'REPLIED_NOT_AVAILABLE'
        );
        const repliedOn = params.row.matchedOn;

        return (
          <StyledBoxFlexColumnNoGap>
            {(isAvailable || isNotAvailable) && (
              <StyledBoxFlexCenterSmallGapFullWidth>
                <StyledNotificationDot isAvailable={isAvailable} />
                <Typography variant="body2" component="span">
                  {isAvailable ? t('available') : t('notAvailable')}
                </Typography>
              </StyledBoxFlexCenterSmallGapFullWidth>
            )}

            {repliedOn && (
              <Typography variant="body2" component="span">
                {dayjs(repliedOn).format('DD MMM YYYY, HH:mm')}
              </Typography>
            )}
          </StyledBoxFlexColumnNoGap>
        );
      }
    }
  ] as GridColDef<GridValidRowModel>[];
};
